import type { RefObject } from "react";
import { useEffect, useState } from "react";
import { emptyChar, type Character, type Message, type Room } from "@/lib/api";
import { Bubble } from "@/Bubble";
import { CharactersScreen } from "@/CharactersScreen";
import { ChatFooter } from "@/ChatFooter";
import { Button } from "@/components/ui/button";
import { Menu, MessagesSquare, SlidersHorizontal } from "lucide-react";

const TYPING_PHRASES = ["mengetik…", "memikirkan…", "merangkai kata…"];

function TypingIndicator({ draftLength }: { draftLength: number }) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const intervalMs = 900 + Math.min(draftLength * 12, 2400);
  useEffect(() => {
    const timer = setInterval(() => {
      setPhraseIndex((current) => (current + 1) % TYPING_PHRASES.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return (
    <div className="text-sm text-muted-foreground animate-pulse">
      {TYPING_PHRASES[phraseIndex]}
    </div>
  );
}

export function MainArea({
  view,
  activeRoom,
  rooms,
  messages,
  loading,
  input,
  chars,
  editing,
  charById,
  setInput,
  setEditing,
  send,
  saveChar,
  editField,
  endRef,
  onOpenSidebar,
  onAvatarUpload,
  onAvatarRemove,
  onDeleteCharacter,
  onRetryMessage,
  onDeleteMessage,
  onDeleteTurn,
  onEditMessage,
  onOpenRoomActions,
  onImportXml,
  onDownloadTemplate
}: {
  view: "chat" | "chars";
  activeRoom: number | null;
  rooms: Room[];
  messages: Message[];
  loading: boolean;
  input: string;
  chars: Character[];
  editing: Character | null;
  charById: Map<number | undefined, Character>;
  setInput: (v: string) => void;
  setEditing: (c: Character | null) => void;
  send: () => Promise<void>;
  saveChar: () => Promise<void>;
  editField: (k: keyof Character, v: string) => void;
  endRef: RefObject<HTMLDivElement | null>;
  onOpenSidebar: () => void;
  onAvatarUpload: (file: File) => Promise<void>;
  onAvatarRemove: () => Promise<void>;
  onDeleteCharacter: (character: Character) => Promise<void>;
  onRetryMessage: (message: Message) => Promise<void>;
  onDeleteMessage: (message: Message) => Promise<void>;
  onDeleteTurn: (message: Message) => Promise<void>;
  onEditMessage: (message: Message) => Promise<void>;
  onOpenRoomActions: () => void;
  onImportXml: (file: File) => Promise<void>;
  onDownloadTemplate: () => void;
}) {
  const activeRoomData = rooms.find((r) => r.id === activeRoom);
  const cast = (activeRoomData?.character_ids ?? [])
    .map((id) => charById.get(id))
    .filter((character): character is Character => Boolean(character));
  const lastMessage = messages[messages.length - 1];
  const draftLength = lastMessage?.is_draft ? lastMessage.content.length : 0;

  return (
    <main className="flex min-w-0 flex-1 flex-col">
      {view === "chat" ? (
        <>
          <header className="flex min-h-14 items-center gap-3 border-b border-border px-3 py-2 md:justify-center">
            <Button aria-label="Open navigation" variant="ghost" size="icon" className="md:hidden" onClick={onOpenSidebar}>
              <Menu />
            </Button>
            <div className="min-w-0 text-left md:text-center">
              <div className="flex items-center gap-2 truncate font-semibold"><MessagesSquare size={16} className="shrink-0 text-primary" />{activeRoomData?.name ?? "Room"}</div>
              {cast.length > 0 && (
                <div className="mt-1 flex items-center gap-1.5 overflow-hidden text-xs text-muted-foreground md:justify-center">
                  {cast.map((character) => <span key={character.id} className="truncate" style={{ color: character.color }}>{character.avatar_emoji} {character.name}</span>)}
                </div>
              )}
            </div>
            <Button aria-label="Chat actions" title="Chat actions" variant="ghost" size="icon" className="ml-auto md:absolute md:right-3" onClick={onOpenRoomActions}>
              <SlidersHorizontal />
            </Button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
            <div className="flex flex-col gap-3 pb-10">
              {messages.map((m) => (
                <Bubble
                  key={m.id}
                  m={m}
                  charById={charById}
                  onRetry={m.is_error ? () => void onRetryMessage(m) : undefined}
                  onDelete={m.is_error ? () => void onDeleteMessage(m) : undefined}
                  onDeleteTurn={!m.is_error && m.turn_id != null ? () => void onDeleteTurn(m) : undefined}
                  onEdit={!m.is_error && m.role === "user" && m.turn_id != null ? () => void onEditMessage(m) : undefined}
                />
              ))}
              {loading && <TypingIndicator draftLength={draftLength} />}
              <div ref={endRef} />
            </div>
          </div>
          <ChatFooter input={input} loading={loading} onInput={setInput} onSend={send} />
        </>
      ) : (
        <CharactersScreen
          chars={chars}
          editing={editing}
          onEdit={setEditing}
          onNew={() => setEditing(emptyChar())}
          onSave={() => void saveChar()}
          onDelete={(c) => void onDeleteCharacter(c)}
          editField={editField}
          onAvatarUpload={onAvatarUpload}
          onAvatarRemove={onAvatarRemove}
          onOpenSidebar={onOpenSidebar}
          onImportXml={onImportXml}
          onDownloadTemplate={onDownloadTemplate}
        />
      )}
    </main>
  );
}